import React, { useState, useRef } from 'react'
import { useParams } from "react-router-dom";
import { createPortal } from 'react-dom';
import { inspectionAPI } from '../../api';
import OptionsMenu from '../options-menu/OptionsMenu';
import { formatInspectionDateToYMD, toPrettyDateStr, toStandardWplTimestamp } from '../../prettyDate';
import WplInput from '../wpl-input/WplInput';
import ArrowAnimation from '../arrow-animation/ArrowAnimation';
import editIconGray from '../../assets/img/edit-gray.png';
import optionIcon from '../../assets/img/option-icon.png'
import deleteIconGray from '../../assets/img/delete-gray.png';
import './inspection-overviev.css'

export default function InspectionOverview({ inspections, updateInspection, editMode, setEditMode, datePickerRef, openDatePicker, downloadAttachment, documentLoading, turbine, setTurbinePopup, refetchTurbineData }) {
    const iconRefs = useRef({});
    const [expandedInspectionIds, setExpandedInspectionIds] = useState([]);
    const [showOptionsId, setShowOptionsId] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [newInspection, setNewInspection] = useState({
        inspection_date: toPrettyDateStr(new Date()),
        due_date: '',
        inspection_type: '',
        inspection_method: '',
        inspection_status: '',
        inspection_reason: ''
    });
    const [loadingInspectionId, setLoadingInspectionId] = useState(null);

    let { windfarm_id } = useParams();
    if (windfarm_id) {
        windfarm_id = parseInt(windfarm_id);
    }

    const handleDeleteInspection = async (inspection_id, windfarm_id) => {
        try {
            const res = await inspectionAPI.deleteInspection(inspection_id, windfarm_id);
            if (res === "OK") {
                refetchTurbineData(turbine.id);
                setTurbinePopup({ show: false });
            }
        } catch (error) {
            console.error("Error deleting inspection:", error);
            setTurbinePopup({
                show: true,
                status: 'wrong',
                title: 'Something went wrong',
                buttonText: 'Close',
                buttonStatus: 'wrong',
                text: error.response?.data
            });
        }
    };


    const handleListActionsForInspection = async (inspection_id) => {
        setLoadingInspectionId(inspection_id);
        try {
            const res = await inspectionAPI.listActionsForInspection(inspection_id);
            if (res) {
                setTurbinePopup({
                    show: true,
                    status: 'warning',
                    title: 'Are you sure you want to delete this inspection?',
                    buttonText: 'Cancel',
                    buttonStatus: 'success',
                    action: () => { setTurbinePopup({ show: false }) },
                    buttonText2: 'Delete',
                    buttonStatus2: 'wrong',
                    action2: () => {
                        handleDeleteInspection(inspection_id, windfarm_id);
                    },
                    text: res.length > 0 ? `Deleting it will also remove ${res.length} associated user actions.` : 'This inspection has no associated user actions and can be deleted.',
                });
            }
        } catch (error) {
            console.error("Error listing actions for inspection:", error);
            setTurbinePopup({
                show: true,
                status: 'wrong',
                title: 'Something went wrong',
                buttonText: 'Close',
                buttonStatus: 'wrong',
                text: error.response?.data
            });
        }
        finally {
            setLoadingInspectionId(null);
        }
    }

    const toggleExpanded = (id) => {
        setExpandedInspectionIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    return (
        <div className='inspection-overview-container'>
            {
                inspections?.length > 0 &&
                <>
                    <div className='inspection-header'>
                        <h2 className="inspection-title">Inspections</h2>
                        {inspections.length > 1 &&
                            turbine?.inspection?.id &&
                            turbine?.inspection?.status !== "completed" &&
                            turbine?.inspection?.status !== "rejected" && (
                                <span className="highlight-label">
                                    The active inspection is highlighted in gray
                                </span>
                            )}
                    </div>

                    <table className="inspection-table">
                        <thead className='inspection-table-header'>
                            <tr>
                                <th>Timestamp created</th>
                                <th>Inspection date</th>
                                <th>Due date</th>
                                <th>Type</th>
                                <th>Method</th>
                                <th>Status</th>
                                <th>Reason</th>
                                <th>Documents</th>
                                {editMode.status ? <th>
                                    <div className='comment-action-container'>
                                        <button className='inspection-btn cancel-btn'
                                            onClick={() => setEditMode({ id: null, status: false })}
                                        >X
                                        </button>
                                        <button className='inspection-btn success-btn'
                                            onClick={() => {
                                                const formattedDate = toStandardWplTimestamp(new Date(newInspection.inspection_date))
                                                updateInspection(
                                                    editMode.id,
                                                    windfarm_id,
                                                    formattedDate,
                                                    newInspection.inspection_type,
                                                    newInspection.inspection_method,
                                                    newInspection.inspection_status,
                                                    newInspection.inspection_reason
                                                )
                                            }
                                            }
                                        >✔
                                        </button>
                                    </div></th> : <th></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                [...inspections]
                                    .sort((a, b) => {
                                        const aIsPending = a.status === 'pending';
                                        const bIsPending = b.status === 'pending';
                                        if (aIsPending && !bIsPending) return -1;
                                        if (!aIsPending && bIsPending) return 1;
                                        return new Date(b.timestamp_created) - new Date(a.timestamp_created);
                                    })
                                    .map((inspection) => {
                                        const isExpanded = expandedInspectionIds.includes(inspection.id);
                                        return (
                                            <React.Fragment key={inspection.id}>
                                                <tr
                                                    className={
                                                        loadingInspectionId === inspection.id
                                                            ? "updating-row"
                                                            : inspections.length > 1 &&
                                                                inspection.id === turbine?.inspection?.id &&
                                                                turbine?.inspection?.status !== "completed" &&
                                                                turbine?.inspection?.status !== "rejected"
                                                                ? "selected-inspection-row"
                                                                : ""
                                                    }
                                                >
                                                    <td>{inspection.timestamp_created ? inspection.timestamp_created : null}</td>
                                                    <td className="inspection-date-picker-container ">{
                                                        editMode.status && editMode.id === inspection.id ?
                                                            <WplInput
                                                                inputRef={datePickerRef}
                                                                onClick={openDatePicker}
                                                                className="performed-inspection-date"
                                                                type="date"
                                                                value={newInspection.inspection_date ?? ""}
                                                                onChange={(e) =>
                                                                    setNewInspection((prev) => ({
                                                                        ...prev,
                                                                        inspection_date: e.target.value || null,
                                                                    }))
                                                                }
                                                            />
                                                            : formatInspectionDateToYMD(inspection?.inspection_date)
                                                    }</td>
                                                    <td>{formatInspectionDateToYMD(inspection?.due_date)}</td>
                                                    <td>
                                                        {editMode.status && editMode.id === inspection.id ?
                                                            <select
                                                                className="inspection-input-cell"
                                                                value={newInspection.inspection_type ?? ""}
                                                                onChange={(e) =>
                                                                    setNewInspection((prev) => ({
                                                                        ...prev,
                                                                        inspection_type: e.target.value || null,
                                                                    }))
                                                                }
                                                            >
                                                                <option value="internal">Internal</option>
                                                                <option value="external">External</option>
                                                                <option value="">N/A</option>
                                                            </select>
                                                            : inspection.type ? inspection.type : 'N/A'}
                                                    </td>
                                                    <td>
                                                        {editMode.status && editMode.id === inspection.id ?
                                                            <select
                                                                className="inspection-input-cell"
                                                                value={newInspection.inspection_method ?? ""}
                                                                onChange={(e) =>
                                                                    setNewInspection((prev) => ({
                                                                        ...prev,
                                                                        inspection_method: e.target.value || null,
                                                                    }))
                                                                }
                                                            >
                                                                <option value="ground">Ground</option>
                                                                <option value="drone">Drone</option>
                                                                <option value="">N/A</option>
                                                            </select>
                                                            : inspection.method ? inspection.method : 'N/A'}
                                                    </td>
                                                    <td>
                                                        {editMode.status && editMode.id === inspection.id ?
                                                            <select
                                                                className="inspection-input-cell"
                                                                value={newInspection.inspection_status ?? ""}

                                                                onChange={(e) =>
                                                                    setNewInspection((prev) => ({
                                                                        ...prev,
                                                                        inspection_status: e.target.value || null
                                                                    }))
                                                                }
                                                            >
                                                                <option value="pending">Pending</option>
                                                                <option value="scheduled">Scheduled</option>
                                                                <option value="disposition_ready">Disposition ready</option>
                                                                <option value="completed">Completed</option>
                                                                <option value="rejected">Rejected</option>
                                                                <option value="overdue">Overdue</option>
                                                                <option value="">N/A</option>

                                                            </select>
                                                            : inspection.status ? inspection.status : 'N/A'}
                                                    </td>
                                                    <td>
                                                        {editMode.status && editMode.id === inspection.id ? (
                                                            <select
                                                                className="inspection-input-cell"
                                                                value={newInspection.inspection_reason ?? ""}
                                                                onChange={(e) =>
                                                                    setNewInspection((prev) => ({
                                                                        ...prev,
                                                                        inspection_reason: e.target.value,
                                                                    }))
                                                                }
                                                            >
                                                                <option value="lightning">Lightning</option>
                                                                <option value="Maintenance">Maintenance</option>
                                                                <option value="">N/A</option>
                                                            </select>
                                                        ) : (
                                                            inspection.reason ? inspection.reason : "N/A"
                                                        )}
                                                    </td>
                                                    <td className="documents-arrow">
                                                        {inspection?.documents?.length ? <ArrowAnimation
                                                            showMore={isExpanded}
                                                            onClickAction={() => toggleExpanded(inspection.id)}
                                                        /> : null}
                                                    </td>
                                                    <td
                                                        className="option-cell"
                                                        onClick={() => {
                                                            const icon = iconRefs.current[inspection.id];
                                                            if (icon) {
                                                                const rect = icon.getBoundingClientRect();
                                                                const scale = window.devicePixelRatio || 1;
                                                                let top = rect.bottom + window.scrollY;
                                                                let left = rect.left + window.scrollX;
                                                                const isZoomedAndSmallScreen =
                                                                    scale > 1 &&
                                                                    window.matchMedia('(max-width: 1600px)').matches;

                                                                if (isZoomedAndSmallScreen) {
                                                                    const adjustment = scale * 100;
                                                                    top += adjustment;
                                                                    if (scale === 1.5) {
                                                                        left += adjustment;
                                                                    } else {
                                                                        left += adjustment + 35;

                                                                    }
                                                                }
                                                                setMenuPosition({ top, left });
                                                                setShowOptionsId(showOptionsId === inspection.id ? null : inspection.id);
                                                            }
                                                        }}
                                                    >
                                                        <img
                                                            src={optionIcon}
                                                            className='action-icon option-btn'
                                                            ref={(el) => (iconRefs.current[inspection.id] = el)}
                                                        />
                                                    </td>
                                                </tr>

                                                {isExpanded && (
                                                    <tr className="subtable-row">
                                                        <td colSpan={10}>
                                                            <div className="subtable-wrapper">
                                                                <table className="inspection-table">
                                                                    <thead className='inspection-table-header'>
                                                                        <tr>
                                                                            <th>Document name</th>
                                                                            <th>Uploaded at</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {inspection.documents?.length > 0 ? (
                                                                            inspection.documents.map((doc, idx) => (
                                                                                <tr key={doc.id ?? `doc-${idx}`}>
                                                                                    <td >
                                                                                        <div onClick={() => downloadAttachment(doc.id)}
                                                                                            className={
                                                                                                doc.name
                                                                                                    ? `document-name${documentLoading.id === doc.id && documentLoading.status ? " document-loading" : ""}`
                                                                                                    : ""
                                                                                            }
                                                                                        >
                                                                                            {doc.name}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td>{formatInspectionDateToYMD(doc.upload_timestamp)}</td>
                                                                                </tr>
                                                                            ))
                                                                        ) : (
                                                                            <tr>
                                                                                <td colSpan={2}>No documents found</td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        )
                                    }
                                    )}

                        </tbody>
                    </table>

                    {showOptionsId && createPortal(
                        <div
                            className="options-container"
                            style={{
                                position: 'absolute',
                                top: menuPosition.top,
                                left: menuPosition.left,
                                zIndex: 9999,
                            }}
                        >
                            <OptionsMenu
                                options={[
                                    {
                                        label: 'Edit',
                                        onClick: () => {
                                            const inspection = inspections.find(i => i.id === showOptionsId)
                                            if (inspection) {
                                                setNewInspection({
                                                    inspection_date: toPrettyDateStr(new Date(inspection.inspection_date)),
                                                    inspection_type: inspection.type,
                                                    inspection_method: inspection.method,
                                                    inspection_status: inspection.status,
                                                    inspection_reason: inspection.reason,
                                                });
                                                setEditMode({ id: showOptionsId, status: true });
                                            }
                                        },
                                        img: editIconGray
                                    },
                                    {
                                        label: 'Delete',
                                        onClick: () => {
                                            handleListActionsForInspection(showOptionsId)
                                            setShowOptionsId(null);
                                        },
                                        img: deleteIconGray
                                    }
                                ]}
                                onClose={() => setShowOptionsId(null)}
                            />
                        </div>,
                        document.body
                    )}

                </>
            }
        </div>
    )
}

